import { Dispatch, SetStateAction, useState } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Input from '@mui/joy/Input';
import IconButton from '@mui/joy/IconButton';
import Select, { MultiValue } from 'react-select';
import { RunTestBulkInitialValues } from '../RunTestBulk';
import { ValidateImportedTests, ValidationError } from '../ValidateImportedTests';
import { Test } from '../RunTest';
import { PageType } from '../RunTest';
import { filterOptions, filterValue } from '../TestFilterValues/TestFilterValues';
import { Delete, Edit, Check } from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';

interface ImportedTestsPreview {
  importedTests: Test[];
  setValues: Dispatch<SetStateAction<RunTestBulkInitialValues>>;
  setWrongUrls: Dispatch<SetStateAction<{ url: string; message: string }[]>>;
  pageTypes: PageType[];
}

const ImportedTestsPreview = ({ importedTests, setValues, setWrongUrls, pageTypes }: ImportedTestsPreview) => {
  const { isSuperAdmin } = useAuth();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState<string>('');
  const [editPageType, setEditPageType] = useState<string>('');
  const [editFilter, setEditFilter] = useState<string>('');

  const clearImportedTests = (setValues: Dispatch<SetStateAction<RunTestBulkInitialValues>>) => {
    setValues((prev) => ({
      ...prev,
      importedTests: []
    }));
  };

  const handleDeleteImportedTest = (index: number) => {
    const updatedTests = importedTests.filter((_, i) => i !== index);
    setValues((prev) => ({
      ...prev,
      importedTests: updatedTests
    }));
  };

  const handleEditImportedTest = (index: number, name: string, pageType: string, filter: string) => {
    setEditIndex(index);
    setEditUrl(name);
    setEditPageType(pageType);
    setEditFilter(filter);
  };

  const editPageTypeChanged = (pageType: string) => {
    setEditPageType(pageType);
    setEditFilter('');
  };

  const findPageType = (pageType: string) => {
    return pageTypes.find((type) => type.value === pageType);
  };

  const handleTestFilter = (value: MultiValue<{ value: string; label: string }>) => {
    if (!value) return;

    const mergedValue = value.map((option) => option.value).join('&');
    setEditFilter(mergedValue);
  };

  const handleSaveEdit = async () => {
    const updatedTests = [...importedTests];
    updatedTests[editIndex!] = {
      ...updatedTests[editIndex!],
      name: editUrl.trim(),
      pageType: editPageType,
      label: editFilter
    };
    try {
      const validatedTests = await ValidateImportedTests(updatedTests, isSuperAdmin);
      setValues((prev) => ({
        ...prev,
        importedTests: validatedTests
      }));
      setEditIndex(null);
      setWrongUrls([]);
    } catch (validationError) {
      if (Array.isArray(validationError)) {
        const wrongUrls = validationError.map((error: ValidationError) => ({
          url: error.value,
          message: error.message
        }));
        setWrongUrls(wrongUrls);
      }
    }
  };

  const disableSaveButton = !editUrl || editUrl === '';

  if (!importedTests.length) return;

  return (
    <Box mb={5}>
      <Typography mt={0} color="success">
        {importedTests.length} test URL(s) have been imported successfully.
      </Typography>
      <Box sx={{ marginBottom: 2 }}>
        <Table sx={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '45%' }}>Link</th>
              <th style={{ width: '15%' }}>Page Type</th>
              <th style={{ width: '25%' }}>Filter</th>
              <th style={{ width: '10%' }}></th>
            </tr>
          </thead>
          <tbody>
            {importedTests.map((test, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td style={{ lineBreak: 'anywhere' }}>
                  {editIndex === index ? (
                    <Input
                      sx={{ width: '100%' }}
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    test.name
                  )}
                </td>
                <td>
                  {editIndex === index ? (
                    <Select
                      placeholder="Select page type"
                      options={pageTypes}
                      value={findPageType(editPageType)}
                      onChange={(selectedOption) => selectedOption && editPageTypeChanged(selectedOption.value)}
                    />
                  ) : (
                    test.pageType
                  )}
                </td>
                <td>
                  {editIndex === index ? (
                    <Select
                      placeholder="Select filter"
                      isClearable={true}
                      isMulti={true}
                      options={filterOptions(editPageType)}
                      value={filterValue(editFilter)}
                      onChange={(value) => handleTestFilter(value)}
                    />
                  ) : (
                    test.label.replaceAll('&', ' / ')
                  )}
                </td>
                <td>
                  <>
                    {editIndex === index ? (
                      <IconButton
                        type="button"
                        color="success"
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={disableSaveButton}
                      >
                        <Check />
                      </IconButton>
                    ) : (
                      <Box display={'flex'} gap={1}>
                        <IconButton
                          type="button"
                          color="neutral"
                          size="sm"
                          onClick={() =>
                            handleEditImportedTest(index, test.name, test.pageType, test.label.replaceAll(' / ', '&'))
                          }
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          type="button"
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteImportedTest(index)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Box>
      <Box display={'flex'} justifyContent={'flex-end'}>
        <button
          className="button button--default button--inverseBlack"
          type="button"
          onClick={() => clearImportedTests(setValues)}
        >
          Clear imported tests
        </button>
      </Box>
    </Box>
  );
};

export default ImportedTestsPreview;
